import { documentToHtmlString } from '@contentful/rich-text-html-renderer';
import chunk from 'lodash/chunk';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import type { UGCItem } from '@/pages/api/product/ugc/[reference]/[collectionName]';

import { CMSSection } from '@/cms/CMSSection';
import { Loader } from '@/components/Loader';
import { SectionHeader } from '@/components/SectionHeader';
import type { ContentfulPlaceholderFragment } from '@/graphql/contentful/api';
import { usePhotoSwipe } from '@/hooks/usePhotoSwipe';
import type { PlaceholderType } from '@/integrations/contentful/types';
import type { GetImageDimensions } from '@/lib/images';
import { getImageDimensions } from '@/lib/images';
import { generateDataTestId } from '@/lib/tests';
import type { SetState } from '@/lib/types';

import * as M from './messages';
import * as S from './UGC.styles';

export type UGCOpts = {
  collectionName: string;
  name: string;
  reference: string;
  type: PlaceholderType;
};

type UGCProps = UGCOpts & ContentfulPlaceholderFragment;

type UGCImage = UGCItem & Partial<GetImageDimensions>;

export const UGC = ({
  collectionName,
  heading,
  name,
  reference,
  subtext,
  type,
}: UGCProps) => {
  const [data, setData] = useState<UGCImage[] | undefined>();
  const { formatMessage } = useIntl();
  const isPhotoSwipeReady = !!data?.every(({ height }) => height);
  const { gallery } = usePhotoSwipe(isPhotoSwipeReady, 'ugc-gallery');

  useEffect(() => {
    (async () => {
      const data = await fetch(
        `/api/product/ugc/${reference}/${collectionName}/`,
      );
      const items = (await data.json()) as UGCItem[];

      setData(items);
    })();
  }, []);

  useEffect(() => {
    if (data?.length) {
      (async () => {
        await updateImagesDimensions(data, setData);
      })();
    }
  }, [data?.length]);

  return (
    <CMSSection
      {...generateDataTestId('section', type)}
      data-testid={'UGCSection' + type}
    >
      <S.Wrapper>
        <S.HeaderWrapper>
          <SectionHeader
            disableGutters
            text={formatMessage(
              heading === ''
                ? M.defined.header
                : {
                    id: M.defined.header.id,
                    defaultMessage: heading as string,
                  },
              { name },
            )}
            textAlign='center'
          />
        </S.HeaderWrapper>

        {subtext && (
          <S.Description
            dangerouslySetInnerHTML={{
              __html: documentToHtmlString(subtext.json),
            }}
            data-test-id='BannerText'
          />
        )}
        <S.PhotosContainer id={gallery}>
          {!data ? (
            <Loader data-testid='UGCLoader' height={450} />
          ) : (
            renderTiles(!isPhotoSwipeReady, data)
          )}
        </S.PhotosContainer>
      </S.Wrapper>
    </CMSSection>
  );
};

const updateImagesDimensions = async (
  data: UGCImage[],
  setData: SetState<UGCImage[] | undefined>,
) => {
  const imagesWithoutDimensions = data.filter(({ height }) => !height);
  const imagesWithDimensions = await Promise.all(
    imagesWithoutDimensions.map(async ({ id, imgSrc }) => {
      const dimensions = await getImageDimensions(imgSrc);
      return { ...dimensions, id };
    }),
  );

  setData((data) =>
    data?.map((item) => {
      const match = imagesWithDimensions.find(({ id }) => id === item.id);
      if (match) {
        item = { ...item, ...match };
      }
      return item;
    }),
  );
};

const renderTiles = (areImagesReady: boolean, items: UGCImage[]) => {
  const chunks = chunk(items, 4);

  return chunks.map((items, i) => (
    // prettier-ignore
    <S.Column key={i} data-testid={`UGCColumn-${i}`}>  {/* NOSONAR */}
      <S.Grid>
        {items.map(({ height, imgSrc, thumbnailSrc, username, width }) => (
          <S.GridItem
            key={imgSrc}
            data-pswp-height={height}
            data-pswp-width={width}
            href={imgSrc}
            onClick={(evt) => {
              if (areImagesReady) {
                evt.preventDefault();
                evt.stopPropagation();
              }
            }}
            processing={areImagesReady}
            rel='noopener noreferrer'
            target='_blank'
          >
            <S.Image
              alt={`@${username}`}
              data-testid={`UGCTile-${imgSrc}`}
              height={0}
              loaderHeight={100}
              sizes='20vw'
              src={thumbnailSrc}
              width={0}
            />
            <S.Caption>@{username}</S.Caption>
          </S.GridItem>
        ))}
      </S.Grid>
    </S.Column>
  ));
};
